import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { ApprovalsPanel } from "../features/approvals/ApprovalsPanel";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";
import type { components, paths } from "../lib/api-types";

type AuditStatus = { files: Array<Record<string, any>> } & Record<string, any>; // generierter Typ fehlt in openapi-Types
type AuditChainStatus = components["schemas"]["AuditChainStatus"];
type AuditIntegrityReport = components["schemas"]["AuditIntegrityReport"];
type TenantIsolationStatus = components["schemas"]["TenantIsolationStatus"];
type AuditRecord = components["schemas"]["AuditRecord"];
type AuditRecordLineage = components["schemas"]["AuditRecordLineage"];
type EvidenceBundle = components["schemas"]["EvidenceBundle"];
type EvidenceLineage = components["schemas"]["EvidenceLineage"];
type EvidenceManifest = components["schemas"]["EvidenceManifest"];
type EvidenceVerification = components["schemas"]["EvidenceVerification"];
type AgentRun = components["schemas"]["AgentRun"];
type AgentRunEvidenceVerification = components["schemas"]["AgentRunEvidenceVerification"];
type AgentRunLineage = components["schemas"]["AgentRunLineage"];
type AgentRunReplay = components["schemas"]["AgentRunReplay"];
type ExportJob = components["schemas"]["ExportJob"];
type AuditKind = string; // Pfad /audit/records fehlt in generierten Types
type AuditDeepLink = { to: string; label: string };

const AUDIT_KIND_LABEL: Record<AuditKind, string> = {
  audit_reseals: "Reseals",
  agent_runs: "Agent Runs",
  agent_replays: "Replays",
  evidence: "Evidence",
  events: "Events",
  fria_dossiers: "FRIA",
  roi_simulations: "ROI",
  roi_sensitivity_reports: "ROI Sensitivity",
  source_quality: "Quality",
  model_routing_decisions: "Model Routing",
  transformation_scope_checks: "Transformation Scope",
  briefings: "Briefings",
  briefing_verifications: "Briefing Gates",
  exports: "Exports",
};

const AUDIT_KIND_ORDER: readonly AuditKind[] = [
  "agent_runs",
  "agent_replays",
  "evidence",
  "roi_simulations",
  "roi_sensitivity_reports",
  "model_routing_decisions",
  "transformation_scope_checks",
  "fria_dossiers",
  "source_quality",
  "briefings",
  "briefing_verifications",
  "exports",
  "events",
  "audit_reseals",
];

const REPLAYABLE_AGENTS = new Set(["roi-monte-carlo", "transformation-scope-approval", "data-quality", "briefing-generator", "compliance-fria"]);
const REPLAYABLE_EVIDENCE_KINDS = new Set(["roi_simulation", "source_quality", "briefing", "fria_dossier", "transformation_scope"]);

function isAuditKind(value: string | null): value is AuditKind {
  return Boolean(value && value in AUDIT_KIND_LABEL);
}

function outputText(run: AgentRun, key: string): string | null {
  const outputs = run.outputs;
  if (!outputs || typeof outputs !== "object" || Array.isArray(outputs)) return null;
  const value = (outputs as Record<string, unknown>)[key];
  if (value === null || value === undefined) return null;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return String(value);
  return null;
}

function runtimeText(run: AgentRun, key: "model_route" | "prompt_version" | "evalset_id" | "audit_namespace"): string | null {
  return run[key] ?? outputText(run, key);
}

function formatRunTime(value?: string | null): string {
  if (!value) return "offen";
  const time = new Date(value);
  return Number.isNaN(time.valueOf()) ? value : time.toLocaleString("de-DE");
}

function auditRecordId(record: AuditRecord): string {
  return record.item_id ?? record.audit_id;
}

function auditRecordItemString(record: AuditRecord, key: string): string | null {
  const value = record.item?.[key];
  return typeof value === "string" && value.trim() ? value : null;
}

function auditRecordMatches(record: AuditRecord, focus: string): boolean {
  return record.audit_id === focus || record.item_id === focus || auditRecordItemString(record, "id") === focus;
}

function auditRecordDeepLinks(record: AuditRecord): AuditDeepLink[] {
  const itemId = auditRecordItemString(record, "id") ?? record.item_id ?? "";
  const runId = auditRecordItemString(record, "run_id");
  const replayId = auditRecordItemString(record, "replay_id") ?? (record.kind === "agent_replays" ? itemId : null);
  const evidenceId = auditRecordItemString(record, "evidence_id") ?? (record.kind === "evidence" ? itemId : null);
  const simulationId = auditRecordItemString(record, "simulation_id") ?? (record.kind === "roi_simulations" ? itemId : null);
  const sourceId = auditRecordItemString(record, "source_id");
  const briefingId = auditRecordItemString(record, "briefing_id") ?? (record.kind === "briefings" ? itemId : null);
  const exportId = auditRecordItemString(record, "export_id") ?? (record.kind === "exports" ? itemId : null);
  const links: AuditDeepLink[] = [];

  if (record.kind === "agent_replays" && runId && replayId) links.push({ to: `/app/agents?run=${encodeURIComponent(runId)}&replay=${encodeURIComponent(replayId)}`, label: "Replay" });
  if (record.kind === "evidence" && evidenceId) links.push({ to: `/app/agents?evidence=${encodeURIComponent(evidenceId)}`, label: "Evidence" });
  if ((record.kind === "roi_simulations" || record.kind === "roi_sensitivity_reports") && simulationId) links.push({ to: `/app/roi?simulation=${encodeURIComponent(simulationId)}`, label: "ROI" });
  if (record.kind === "source_quality" && sourceId) links.push({ to: `/app/sources/${encodeURIComponent(sourceId)}/quality`, label: "Quality" });
  if (record.kind === "briefings" && briefingId) links.push({ to: `/app/briefings/${encodeURIComponent(briefingId)}`, label: "Briefing" });
  if (record.kind === "briefing_verifications" && briefingId) links.push({ to: `/app/briefings/${encodeURIComponent(briefingId)}`, label: "Citation Gate" });
  if (record.kind === "exports" && exportId) links.push({ to: `/app/agents?export=${encodeURIComponent(exportId)}`, label: "Export" });
  if (record.kind === "model_routing_decisions") links.push({ to: "/app/compliance", label: "Model Gate" });
  if (record.kind === "transformation_scope_checks") links.push({ to: "/app/transformation", label: "Scope Gate" });
  if (record.kind === "fria_dossiers" || record.kind === "audit_reseals") links.push({ to: "/app/compliance", label: record.kind === "fria_dossiers" ? "FRIA" : "Reseal" });

  return links.filter((link, index, all) => all.findIndex((candidate) => candidate.to === link.to && candidate.label === link.label) === index);
}

function replayIdFromEvidenceRefs(bundle: EvidenceBundle): string | null {
  const match = (bundle.source_refs ?? [])
    .map((ref) => ref.match(/^store:\/\/[^/]+\/agent_replays\/([^/]+)$/)?.[1] ?? null)
    .find(Boolean);
  return match ?? null;
}

export default function AgentRunsPage() {
  return (
    <div className="page">
      <h1>Agent Runs &amp; Approvals</h1>
      <p className="subtitle">
        Genehmigung, Ablehnung und Audit-Log aller Action-Runs (Mahnlauf, Pricing-Review, Lieferanten-Eskalation, Marketing-Budget-Shift). SelfCLAW: keine Aktion ohne explizites Approve.
      </p>
      <ApprovalsPanel />
      <AuditEvidencePanel />
    </div>
  );
}

function AuditEvidencePanel() {
  const api = useApi();
  const qc = useQueryClient();
  const { effectiveTenantId } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const [kind, setKind] = useState<AuditKind>(() => {
    const requestedKind = searchParams.get("kind");
    return isAuditKind(requestedKind) ? requestedKind : "agent_runs";
  });
  const [replayResult, setReplayResult] = useState<AgentRunReplay | null>(null);
  const runFocus = searchParams.get("run")?.trim() ?? "";
  const replayFocus = searchParams.get("replay")?.trim() ?? "";
  const agentFocus = searchParams.get("agent")?.trim() ?? "";
  const evidenceFocus = searchParams.get("evidence")?.trim() ?? "";
  const exportFocus = searchParams.get("export")?.trim() ?? "";
  const recordFocus = searchParams.get("record")?.trim() ?? "";

  useEffect(() => {
    const requestedKind = searchParams.get("kind");
    if (isAuditKind(requestedKind) && requestedKind !== kind) setKind(requestedKind);
    if (!requestedKind && kind !== "agent_runs") setKind("agent_runs");
  }, [kind, searchParams]);

  const selectAuditKind = (nextKind: AuditKind) => {
    setKind(nextKind);
    const nextParams = new URLSearchParams(searchParams);
    if (nextKind === "agent_runs") nextParams.delete("kind");
    else nextParams.set("kind", nextKind);
    setSearchParams(nextParams, { replace: true });
  };

  const status = useQuery({
    queryKey: ["audit-status", effectiveTenantId],
    queryFn: () => api.get<AuditStatus>("/audit/status"),
    refetchInterval: 30_000,
  });

  const isolation = useQuery({
    queryKey: ["agent-evidence-tenant-isolation", effectiveTenantId],
    queryFn: () => api.get<TenantIsolationStatus>("/tenant-isolation/status"),
    refetchInterval: 30_000,
  });

  const records = useQuery({
    queryKey: ["audit-records", effectiveTenantId, kind],
    queryFn: () => api.get<AuditRecord[]>(`/audit/records?kind=${kind}&limit=8`),
    refetchInterval: 30_000,
  });

  const recordDetail = useQuery({
    queryKey: ["audit-record-detail", effectiveTenantId, kind, recordFocus],
    queryFn: () => api.get<AuditRecord>(`/audit/records/${encodeURIComponent(recordFocus)}?kind=${kind}`),
    enabled: Boolean(recordFocus),
    retry: false,
    refetchInterval: 30_000,
  });

  const recordLineage = useQuery({
    queryKey: ["audit-record-lineage", effectiveTenantId, kind, recordFocus],
    queryFn: () => api.get<AuditRecordLineage>(`/audit/records/${encodeURIComponent(recordFocus)}/lineage?kind=${kind}`),
    enabled: Boolean(recordFocus),
    retry: false,
    refetchInterval: 30_000,
  });

  const chainStatus = useQuery({
    queryKey: ["audit-chain-status", effectiveTenantId, kind],
    queryFn: () => api.get<AuditChainStatus>(`/audit/chain-status?kind=${kind}`),
    refetchInterval: 30_000,
  });

  const integrityReport = useQuery({
    queryKey: ["audit-integrity-report", effectiveTenantId],
    queryFn: () => api.get<AuditIntegrityReport>("/audit/integrity-report"),
    refetchInterval: 30_000,
  });

  const evidence = useQuery({
    queryKey: ["evidence-registry", effectiveTenantId],
    queryFn: () => api.get<EvidenceBundle[]>("/evidence?limit=6"),
    refetchInterval: 30_000,
  });

  const exports = useQuery({
    queryKey: ["exports", effectiveTenantId],
    queryFn: () => api.get<ExportJob[]>("/exports?limit=6"),
    refetchInterval: 30_000,
  });

  const runs = useQuery({
    queryKey: ["agent-runs", effectiveTenantId],
    queryFn: () => api.get<AgentRun[]>("/agent-runs"),
    refetchInterval: 30_000,
  });

  const runReplays = useQuery({
    queryKey: ["agent-run-replays", effectiveTenantId, runFocus],
    queryFn: () => api.get<AgentRunReplay[]>(`/agent-runs/${encodeURIComponent(runFocus)}/replays?limit=4`),
    enabled: Boolean(runFocus),
    retry: false,
    refetchInterval: 30_000,
  });

  const runLineage = useQuery({
    queryKey: ["agent-run-lineage", effectiveTenantId, runFocus],
    queryFn: () => api.get<AgentRunLineage>(`/agent-runs/${encodeURIComponent(runFocus)}/lineage`),
    enabled: Boolean(runFocus),
    retry: false,
    refetchInterval: 30_000,
  });

  const runEvidenceGate = useQuery({
    queryKey: ["agent-run-evidence-verification", effectiveTenantId, runFocus],
    queryFn: () => api.get<AgentRunEvidenceVerification>(`/agent-runs/${encodeURIComponent(runFocus)}/evidence-verification`),
    enabled: Boolean(runFocus),
    retry: false,
    refetchInterval: 30_000,
  });

  const replayDetail = useQuery({
    queryKey: ["agent-run-replay-detail", effectiveTenantId, runFocus, replayFocus],
    queryFn: () => api.get<AgentRunReplay>(`/agent-runs/${encodeURIComponent(runFocus)}/replays/${encodeURIComponent(replayFocus)}`),
    enabled: Boolean(runFocus && replayFocus),
    retry: false,
    refetchInterval: 30_000,
  });

  const evidenceDetail = useQuery({
    queryKey: ["evidence-detail", effectiveTenantId, evidenceFocus],
    queryFn: () => api.get<EvidenceBundle>(`/evidence/${encodeURIComponent(evidenceFocus)}`),
    enabled: Boolean(evidenceFocus),
    retry: false,
  });

  const evidenceLineage = useQuery({
    queryKey: ["evidence-lineage", effectiveTenantId, evidenceFocus],
    queryFn: () => api.get<EvidenceLineage>(`/evidence/${encodeURIComponent(evidenceFocus)}/lineage`),
    enabled: Boolean(evidenceFocus),
    retry: false,
  });

  const evidenceManifest = useQuery({
    queryKey: ["evidence-manifest", effectiveTenantId, evidenceFocus],
    queryFn: () => api.get<EvidenceManifest>(`/evidence/${encodeURIComponent(evidenceFocus)}/manifest`),
    enabled: Boolean(evidenceFocus),
    retry: false,
  });

  const evidenceVerification = useQuery({
    queryKey: ["evidence-verification", effectiveTenantId, evidenceFocus],
    queryFn: () => api.get<EvidenceVerification>(`/evidence/${encodeURIComponent(evidenceFocus)}/verify`),
    enabled: Boolean(evidenceFocus),
    retry: false,
  });

  const exportDetail = useQuery({
    queryKey: ["export-detail", effectiveTenantId, exportFocus],
    queryFn: () => api.get<ExportJob>(`/exports/${encodeURIComponent(exportFocus)}`),
    enabled: Boolean(exportFocus),
    retry: false,
  });

  const replayRun = useMutation({
    mutationFn: (runId: string) => api.post<AgentRunReplay>(`/agent-runs/${runId}/replay`, {}),
    onSuccess: (result) => {
      setReplayResult(result);
      qc.invalidateQueries({ queryKey: ["evidence-registry", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["audit-records", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["audit-chain-status", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["agent-runs", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["agent-run-replays", effectiveTenantId] });
      qc.invalidateQueries({ queryKey: ["agent-run-evidence-verification", effectiveTenantId, result.run_id] });
    },
  });

  const files = status.data?.files ?? [];
  const totalRecords = files.reduce((sum, file) => sum + (file.records ?? 0), 0);
  const recordsByKind = new Map(files.map((file) => [file.kind, file.records ?? 0]));
  const evidenceItems = evidence.data ?? [];
  const exportItems = exports.data ?? [];
  const runItems = runs.data ?? [];
  const auditRecords = records.data ?? [];
  const selectedRun = runFocus ? runItems.find((run) => run.run_id === runFocus) : null;
  const selectedReplay = replayFocus ? replayDetail.data ?? (runReplays.data ?? []).find((item) => item.replay_id === replayFocus) ?? null : null;
  const selectedEvidence = evidenceFocus ? evidenceDetail.data ?? evidenceItems.find((item) => item.id === evidenceFocus) ?? null : null;
  const selectedEvidenceReplayRef = evidenceLineage.data?.refs?.find((ref) => ref.ref_type === "agent_replay" && ref.route) ?? null;
  const selectedEvidenceReplayId = selectedEvidenceReplayRef?.item_id ?? (selectedEvidence?.kind === "agent_replay" ? replayIdFromEvidenceRefs(selectedEvidence) : null);
  const selectedExport = exportFocus ? exportDetail.data ?? exportItems.find((item) => item.id === exportFocus) ?? null : null;
  const selectedAuditRecord = recordFocus
    ? recordDetail.data ?? auditRecords.find((record) => auditRecordMatches(record, recordFocus)) ?? null
    : null;
  const selectedAuditLineage = recordLineage.data ?? null;
  const filteredRuns = runItems
    .filter((run) => (runFocus ? run.run_id === runFocus : true))
    .filter((run) => (agentFocus ? run.agent === agentFocus : true))
    .slice(0, 6);
  const focusMissing = Boolean(runFocus && !runs.isLoading && !selectedRun);
  const replayFocusMissing = Boolean(replayFocus && !replayDetail.isLoading && !selectedReplay);
  const artifactFocusMissing = Boolean(
    (evidenceFocus && !evidenceDetail.isLoading && !selectedEvidence) ||
    (exportFocus && !exportDetail.isLoading && !selectedExport),
  );
  const recordFocusMissing = Boolean(recordFocus && !recordDetail.isLoading && !records.isLoading && !selectedAuditRecord);

  return (
    <section className="audit-evidence-panel">
      <div className="audit-evidence-head">
        <div>
          <p className="hero-eyebrow">Persistent Evidence</p>
          <h2>Audit-Speicher für Replay, FRIA und Tenant-Deny.</h2>
          <p>
            Redacted JSONL-Audit je Tenant. Secrets werden vor Persistenz entfernt; andere Tenants erhalten leere Record-Listen statt fremder Daten.
          </p>
        </div>
        <span className={`status-pill ${status.data?.enabled ? "good" : "warning"}`}>
          {status.data?.enabled ? status.data.backend : status.isLoading ? "lade" : "disabled"}
        </span>
      </div>

      <div className="audit-summary-grid">
        <div>
          <span>Tenant</span>
          <strong>{status.data?.tenant_id ?? effectiveTenantId}</strong>
        </div>
        <div>
          <span>Audit-Dateien</span>
          <strong>{files.length}</strong>
        </div>
        <div>
          <span>Records</span>
          <strong>{totalRecords.toLocaleString("de-DE")}</strong>
        </div>
        <div>
          <span>Executive Exports</span>
          <strong>{(exports.data ?? []).length}</strong>
        </div>
        <div>
          <span>Redaction</span>
          <strong>{status.data?.redaction ?? "policy"}</strong>
        </div>
        <div className={`audit-chain-card ${chainStatus.data?.enabled ? chainStatus.data.verified ? "verified" : "broken" : "disabled"}`}>
          <span>Audit Chain</span>
          <strong>{chainStatus.data?.enabled ? chainStatus.data.verified ? "verified" : "broken" : chainStatus.isLoading ? "lade" : "disabled"}</strong>
          <small>{AUDIT_KIND_LABEL[kind]} · {(chainStatus.data?.records ?? 0).toLocaleString("de-DE")} Records</small>
        </div>
      </div>

      {status.error && <p className="error">Audit-Status konnte nicht geladen werden.</p>}
      {chainStatus.data?.error && (
        <p className="error">Audit-Chain-Bruch: {chainStatus.data.error} bei {chainStatus.data.broken_at ?? "unbekannt"}.</p>
      )}

      <AuditIntegrityPanel
        report={integrityReport.data}
        loading={integrityReport.isLoading}
        error={integrityReport.error}
        currentKind={kind}
        onSelectKind={selectAuditKind}
      />

      <TenantIsolationEvidencePanel
        status={isolation.data}
        loading={isolation.isLoading}
        error={isolation.error}
      />

      {replayResult && (
        <div className={`replay-result ${replayResult.status}`}>
          <div>
            <span>Letzter Replay</span>
            <strong>{replayResult.status === "passed" ? "Deterministisch bestanden" : "Drift erkannt"}</strong>
          </div>
          <code>{replayResult.evidence_id}</code>
          <span className="muted small">{replayResult.actual_sha256.slice(0, 16)}</span>
        </div>
      )}

      {(evidenceFocus || exportFocus) && (
        <div className={`run-focus-panel ${selectedEvidence || selectedExport ? "found" : artifactFocusMissing ? "missing" : ""}`}>
          <div className="section-head compact">
            <div>
              <h3>Artifact Focus</h3>
              <p className="muted small">
                {evidenceFocus ? `Evidence ${evidenceFocus}` : `Export ${exportFocus}`}
              </p>
            </div>
            <Link className="btn ghost small" to="/app/agents">Filter löschen</Link>
          </div>

          {selectedEvidence && (
            <div className="run-focus-card">
              <div>
                <span className="hero-eyebrow">{selectedEvidence.kind}</span>
                <h4>{selectedEvidence.id}</h4>
                <p>{selectedEvidence.summary ?? selectedEvidence.uri}</p>
              </div>
              <div className="run-focus-meta">
                <span className="pill good">Evidence</span>
                <code>{selectedEvidence.run_id}</code>
                {selectedEvidence.sha256 && <code>{selectedEvidence.sha256.slice(0, 16)}</code>}
              </div>
              <div className="run-focus-grid">
                <div><span>Tenant</span><strong>{selectedEvidence.tenant_id}</strong></div>
                <div><span>Class</span><strong>{selectedEvidence.data_classification}</strong></div>
                <div><span>Erstellt</span><strong>{formatRunTime(selectedEvidence.created_at)}</strong></div>
                <div><span>Ablauf</span><strong>{formatRunTime(selectedEvidence.expires_at)}</strong></div>
              </div>
              <div className="run-focus-actions">
                <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(selectedEvidence.run_id)}`}>Run öffnen</Link>
                {(selectedEvidenceReplayRef?.route || selectedEvidenceReplayId) && (
                  <Link
                    className="btn ghost small"
                    to={selectedEvidenceReplayRef?.route ?? `/app/agents?run=${encodeURIComponent(selectedEvidence.run_id)}&replay=${encodeURIComponent(selectedEvidenceReplayId ?? "")}`}
                  >
                    Replay Detail öffnen
                  </Link>
                )}
                <a className="btn ghost small" href={selectedEvidence.signed_url} target="_blank" rel="noreferrer">Signed URL</a>
              </div>
              {evidenceManifest.data && (
                <div className="lineage-card evidence-manifest-card">
                  <div className="section-head compact">
                    <h5>Evidence Manifest</h5>
                    <span className="muted small">{evidenceManifest.data.manifest_sha256.slice(0, 16)}</span>
                  </div>
                  <div className="run-focus-grid">
                    <div><span>Tenant scoped</span><strong>{evidenceManifest.data.integrity.tenant_scoped ? "pass" : "fail"}</strong></div>
                    <div><span>Artifact SHA</span><strong>{evidenceManifest.data.integrity.evidence_sha256_present ? "present" : "missing"}</strong></div>
                    <div><span>Source refs</span><strong>{evidenceManifest.data.integrity.source_ref_count}</strong></div>
                    <div><span>Resolved</span><strong>{evidenceManifest.data.integrity.resolved_ref_count}</strong></div>
                    <div><span>Missing</span><strong>{evidenceManifest.data.integrity.missing_ref_count}</strong></div>
                    <div><span>Generated</span><strong>{formatRunTime(evidenceManifest.data.generated_at)}</strong></div>
                  </div>
                </div>
              )}
              {evidenceVerification.data && (
                <div className={`lineage-card evidence-verification-card ${evidenceVerification.data.status}`}>
                  <div className="section-head compact">
                    <h5>Evidence Verification</h5>
                    <span className={`pill ${evidenceVerification.data.status === "pass" ? "good" : "warn"}`}>
                      {evidenceVerification.data.status}
                    </span>
                  </div>
                  <ul className="verification-list">
                    {(evidenceVerification.data.checks ?? []).map((check) => (
                      <li key={check.id}>
                        <span className={`pill ${check.status === "pass" ? "good" : "warn"}`}>{check.status}</span>
                        <div>
                          <strong>{check.title}</strong>
                          <p>{check.detail}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {evidenceLineage.data && (
                <div className="lineage-card">
                  <div className="section-head compact">
                    <h5>Lineage</h5>
                    <span className="muted small">{evidenceLineage.data.refs?.length ?? 0} tenant-lokale Refs</span>
                  </div>
                  <ul className="lineage-list">
                    {(evidenceLineage.data.refs ?? []).map((ref) => (
                      <li key={ref.ref}>
                        <span className={`pill ${ref.exists ? "good" : "warn"}`}>{ref.ref_type}</span>
                        <code>{ref.item_id ?? ref.ref}</code>
                        {ref.route && <Link className="btn ghost small" to={ref.route}>Öffnen</Link>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {selectedExport && (
            <div className="run-focus-card">
              <div>
                <span className="hero-eyebrow">Executive Export</span>
                <h4>{selectedExport.type} · {selectedExport.format.toUpperCase()}</h4>
                <p>{selectedExport.data_policy}</p>
              </div>
              <div className="run-focus-meta">
                <span className={`pill ${selectedExport.status === "succeeded" ? "good" : selectedExport.status === "blocked" ? "warn" : "muted"}`}>{selectedExport.status}</span>
                <code>{selectedExport.id}</code>
                {selectedExport.evidence_id && <code>Evidence {selectedExport.evidence_id}</code>}
              </div>
              <div className="run-focus-grid">
                <div><span>Audience</span><strong>{selectedExport.audience}</strong></div>
                <div><span>Run</span><strong>{selectedExport.run_id ?? "none"}</strong></div>
                <div><span>Erstellt</span><strong>{formatRunTime(selectedExport.created_at)}</strong></div>
                <div><span>Refs</span><strong>{selectedExport.evidence_refs?.length ?? 0}</strong></div>
              </div>
              <div className="run-focus-actions">
                {selectedExport.run_id && <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(selectedExport.run_id)}`}>Run öffnen</Link>}
                {selectedExport.evidence_id && <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(selectedExport.evidence_id)}`}>Evidence öffnen</Link>}
                {selectedExport.signed_url && <a className="btn ghost small" href={selectedExport.signed_url} target="_blank" rel="noreferrer">Artefakt öffnen</a>}
              </div>
            </div>
          )}

          {artifactFocusMissing && (
            <div className="empty-state">Dieses Artefakt ist in diesem Tenant nicht sichtbar. Das ist korrekt, wenn der Link aus einem anderen Tenant stammt oder das Artefakt nicht mehr existiert.</div>
          )}
        </div>
      )}

      {(runFocus || agentFocus) && (
        <div className={`run-focus-panel ${selectedRun ? "found" : focusMissing ? "missing" : ""}`}>
          <div className="section-head compact">
            <div>
              <h3>Run Focus</h3>
              <p className="muted small">
                {runFocus ? `Deep Link ${runFocus}` : `Agent Filter ${agentFocus}`}
              </p>
            </div>
            <Link className="btn ghost small" to="/app/agents">Filter löschen</Link>
          </div>

          {selectedRun && (
            <div className="run-focus-card">
              <div>
                <span className="hero-eyebrow">{selectedRun.agent}</span>
                <h4>{outputText(selectedRun, "summary") ?? selectedRun.run_id}</h4>
                <p>{outputText(selectedRun, "data_policy") ?? selectedRun.evidence_bundle ?? "Tenant-scoped Agent Run mit auditierbarem Status."}</p>
              </div>
              <div className="run-focus-meta">
                <span className={`pill ${selectedRun.status === "succeeded" ? "good" : selectedRun.status === "blocked" ? "warn" : "muted"}`}>{selectedRun.status}</span>
                <code>{selectedRun.run_id}</code>
                {selectedRun.evidence_id && <code>Evidence {selectedRun.evidence_id}</code>}
              </div>
              <div className="run-focus-grid">
                <div><span>Gestartet</span><strong>{formatRunTime(selectedRun.started_at)}</strong></div>
                <div><span>Beendet</span><strong>{formatRunTime(selectedRun.ended_at)}</strong></div>
                <div><span>Route</span><strong>{runtimeText(selectedRun, "model_route") ?? selectedRun.llm_calls?.[0]?.model ?? "local/runtime"}</strong></div>
                <div><span>Prompt</span><strong>{runtimeText(selectedRun, "prompt_version") ?? "unversioniert"}</strong></div>
                <div><span>Evalset</span><strong>{runtimeText(selectedRun, "evalset_id") ?? "nicht gesetzt"}</strong></div>
                <div><span>Trace</span><strong>{selectedRun.trace_id ?? selectedRun.trace_url ?? "lokal"}</strong></div>
                <div><span>Audit</span><strong>{runtimeText(selectedRun, "audit_namespace") ?? selectedRun.tenant_id}</strong></div>
                <div><span>Approval</span><strong>{selectedRun.approval?.required ? selectedRun.approval.granted ? "granted" : "required" : "not required"}</strong></div>
              </div>
              <div className="run-focus-actions">
                {REPLAYABLE_AGENTS.has(selectedRun.agent) && (
                  <button
                    type="button"
                    className="btn ghost small"
                    disabled={replayRun.isPending}
                    onClick={() => replayRun.mutate(selectedRun.run_id)}
                  >
                    {replayRun.isPending ? "Replay laeuft" : "Deterministisch replayen"}
                  </button>
                )}
                {selectedRun.evidence_id && <a className="btn ghost small" href={`#evidence-${selectedRun.evidence_id}`}>Evidence markieren</a>}
              </div>
              {runEvidenceGate.data && (
                <div className={`lineage-card run-evidence-gate ${runEvidenceGate.data.status}`}>
                  <div className="section-head compact">
                    <h5>Run Evidence Gate</h5>
                    <span className={`pill ${runEvidenceGate.data.status === "pass" ? "good" : "warn"}`}>
                      {runEvidenceGate.data.status}
                    </span>
                  </div>
                  <div className="run-focus-grid">
                    <div><span>Nachweise</span><strong>{runEvidenceGate.data.evidence_count}</strong></div>
                    <div><span>Bestanden</span><strong>{runEvidenceGate.data.passed_count}</strong></div>
                    <div><span>Fehlgeschlagen</span><strong>{runEvidenceGate.data.failed_count}</strong></div>
                    <div><span>Geprüft</span><strong>{formatRunTime(runEvidenceGate.data.checked_at)}</strong></div>
                  </div>
                  <ul className="lineage-list">
                    {(runEvidenceGate.data.items ?? []).map((item) => (
                      <li key={item.evidence_id}>
                        <span className={`pill ${item.status === "pass" ? "good" : "warn"}`}>{item.status}</span>
                        <code>{item.kind} · {item.evidence_id}</code>
                        <span className="muted small">{item.manifest_sha256.slice(0, 16)}</span>
                        <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(item.evidence_id)}`}>Evidence öffnen</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedReplay && (
                <div className={`replay-detail-card ${selectedReplay.status}`}>
                  <div className="section-head compact">
                    <div>
                      <h5>Replay Detail</h5>
                      <p className="muted small">{selectedReplay.replay_id} · {formatRunTime(selectedReplay.replayed_at)}</p>
                    </div>
                    <span className={`pill ${selectedReplay.status === "passed" ? "good" : "warn"}`}>{selectedReplay.status}</span>
                  </div>
                  <div className="replay-detail-grid">
                    <div><span>Deterministik</span><strong>{selectedReplay.deterministic ? "seed-stable" : "range-stable"}</strong></div>
                    <div><span>Agent</span><strong>{selectedReplay.agent}</strong></div>
                    <div><span>Expected SHA</span><strong>{selectedReplay.expected_sha256.slice(0, 16)}</strong></div>
                    <div><span>Actual SHA</span><strong>{selectedReplay.actual_sha256.slice(0, 16)}</strong></div>
                  </div>
                  <p className="muted small">
                    {selectedReplay.drift
                      ? `Drift-Felder: ${Object.keys(selectedReplay.drift).join(", ")}`
                      : "Replay stimmt mit dem persistierten Agent-Kontext ueberein."}
                  </p>
                  <div className="run-focus-actions">
                    <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(selectedReplay.evidence_id)}`}>Replay Evidence öffnen</Link>
                    <a className="btn ghost small" href={selectedReplay.evidence_ref} target="_blank" rel="noreferrer">Evidence Ref</a>
                  </div>
                </div>
              )}
              {runLineage.data && (
                <div className="lineage-card">
                  <div className="section-head compact">
                    <h5>Run Lineage</h5>
                    <span className="muted small">
                      {runLineage.data.refs?.length ?? 0} Refs · {runLineage.data.replay_ids?.length ?? 0} Replays
                    </span>
                  </div>
                  <ul className="lineage-list">
                    {(runLineage.data.refs ?? []).slice(0, 8).map((ref) => (
                      <li key={ref.ref}>
                        <span className={`pill ${ref.exists ? "good" : "warn"}`}>{ref.ref_type}</span>
                        <code>{ref.item_id ?? ref.ref}</code>
                        {ref.route && <Link className="btn ghost small" to={ref.route}>Öffnen</Link>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {replayFocusMissing && (
                <div className="empty-state">Dieses Replay ist in diesem Run oder Tenant nicht sichtbar. Das schützt vor Cross-Tenant- und falschen Run-Kontexten.</div>
              )}
              {(runReplays.data ?? []).length > 0 && (
                <div className="replay-history">
                  <strong>Replay Verlauf</strong>
                  {(runReplays.data ?? []).map((item) => (
                    <span key={item.replay_id}>
                      <Link to={`/app/agents?run=${encodeURIComponent(item.run_id)}&replay=${encodeURIComponent(item.replay_id)}`}>
                        {item.status} · {new Date(item.replayed_at).toLocaleString("de-DE")}
                      </Link>
                      {" · "}
                      <Link to={`/app/agents?evidence=${encodeURIComponent(item.evidence_id)}`}>
                        {item.evidence_id}
                      </Link>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {focusMissing && (
            <div className="empty-state">Dieser Run ist in diesem Tenant nicht sichtbar. Das ist korrekt, wenn der Link aus einem anderen Tenant stammt oder der Run bereits geloescht wurde.</div>
          )}
        </div>
      )}

      <div className="run-list-panel">
        <div className="section-head compact">
          <h3>{agentFocus ? `Recent Runs: ${agentFocus}` : "Recent Agent Runs"}</h3>
          <span className="muted small">{filteredRuns.length} von {runItems.length} geladen</span>
        </div>
        <ul className="run-list">
          {filteredRuns.map((run) => (
            <li key={run.run_id} className={run.run_id === runFocus ? "active" : ""}>
              <div>
                <strong>{run.agent}</strong>
                <span>{formatRunTime(run.started_at)} · {runtimeText(run, "model_route") ?? outputText(run, "summary") ?? run.evidence_bundle ?? "Agent Run"}</span>
              </div>
              <div className="run-list-meta">
                <span className={`pill ${run.status === "succeeded" ? "good" : run.status === "blocked" ? "warn" : "muted"}`}>{run.status}</span>
                <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(run.run_id)}`}>Fokus</Link>
              </div>
            </li>
          ))}
        </ul>
        {runs.isLoading && <p className="muted">Agent Runs werden geladen...</p>}
        {!runs.isLoading && filteredRuns.length === 0 && <p className="muted">Keine Agent Runs für diesen Filter.</p>}
      </div>

      <div className="evidence-registry">
        <div className="section-head compact">
          <h3>Evidence Registry</h3>
          <span className="muted small">{(evidence.data ?? []).length} aktuelle Bundles</span>
        </div>
        <ul className="evidence-registry-list">
          {evidenceItems.map((item) => (
            <li key={item.id} id={`evidence-${item.id}`} className={selectedRun?.evidence_id === item.id || evidenceFocus === item.id ? "active" : ""}>
              <div>
                <strong>{item.id}</strong>
                <span>{item.kind} · {item.run_id}</span>
              </div>
              <code>{item.sha256?.slice(0, 16) ?? "no-hash"}</code>
              <p>{item.summary ?? item.uri}</p>
              {REPLAYABLE_EVIDENCE_KINDS.has(item.kind) && (
                <button
                  type="button"
                  className="btn ghost replay-btn"
                  disabled={replayRun.isPending}
                  onClick={() => replayRun.mutate(item.run_id)}
                >
                  {replayRun.isPending ? "Replay laeuft" : "Replay prüfen"}
                </button>
              )}
              <div className="export-registry-links">
                <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(item.id)}`}>Evidence öffnen</Link>
                <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(item.run_id)}`}>Run öffnen</Link>
              </div>
            </li>
          ))}
        </ul>
        {replayRun.error && <p className="error">Replay-Gate konnte diesen Run nicht prüfen.</p>}
        {evidence.isLoading && <p className="muted">Evidence Registry wird geladen...</p>}
        {!evidence.isLoading && (evidence.data ?? []).length === 0 && (
          <p className="muted">Noch keine Evidence-Bundles für diesen Tenant.</p>
        )}
      </div>

      <div className="export-registry">
        <div className="section-head compact">
          <h3>Executive Export Registry</h3>
          <span className="muted small">{(exports.data ?? []).length} aktuelle Pakete</span>
        </div>
        <ul className="export-registry-list">
          {exportItems.map((item) => (
            <li key={item.id} className={`export-registry-item status-${item.status} ${exportFocus === item.id ? "active" : ""}`}>
              <div>
                <strong>{item.type} · {item.format.toUpperCase()}</strong>
                <span>{item.audience} · {new Date(item.created_at).toLocaleString("de-DE")}</span>
              </div>
              <div className="export-registry-meta">
                <span className={`pill ${item.status === "succeeded" ? "good" : item.status === "blocked" ? "warn" : "muted"}`}>
                  {item.status}
                </span>
                <code>{item.id}</code>
              </div>
              <p>{item.data_policy}</p>
              <div className="export-registry-links">
                {item.evidence_id && <code>Evidence {item.evidence_id}</code>}
                {item.run_id && <code>Run {item.run_id}</code>}
                <Link className="btn ghost small" to={`/app/agents?export=${encodeURIComponent(item.id)}`}>Export öffnen</Link>
                {item.run_id && <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(item.run_id)}`}>Run öffnen</Link>}
                {item.evidence_id && <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(item.evidence_id)}`}>Evidence öffnen</Link>}
                {item.signed_url && (
                  <a className="btn ghost small" href={item.signed_url} target="_blank" rel="noreferrer">
                    Artefakt öffnen
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
        {exports.isLoading && <p className="muted">Export Registry wird geladen...</p>}
        {!exports.isLoading && (exports.data ?? []).length === 0 && (
          <p className="muted">Noch keine Executive Exports für diesen Tenant.</p>
        )}
      </div>

      <div className="audit-kind-tabs" role="tablist" aria-label="Audit record kind">
        {AUDIT_KIND_ORDER.map((item) => {
          const count = recordsByKind.get(item) ?? 0;
          return (
            <button
              key={item}
              type="button"
              role="tab"
              aria-selected={kind === item}
              className={`btn ghost audit-kind-tab ${kind === item ? "active" : ""}`}
              onClick={() => selectAuditKind(item)}
            >
              <span>{AUDIT_KIND_LABEL[item]}</span>
              <small>{Number(count).toLocaleString("de-DE")}</small>
            </button>
          );
        })}
      </div>

      {recordFocus && (
        <div className={`run-focus-panel ${selectedAuditRecord ? "found" : recordFocusMissing ? "missing" : ""}`}>
          <div className="section-head compact">
            <div>
              <h3>Audit Record Focus</h3>
              <p className="muted small">{AUDIT_KIND_LABEL[kind]} · {recordFocus}</p>
            </div>
            <Link className="btn ghost small" to={`/app/agents${kind === "agent_runs" ? "" : `?kind=${encodeURIComponent(kind)}`}`}>Fokus löschen</Link>
          </div>

          {selectedAuditRecord && (
            <div className="run-focus-card">
              <div>
                <span className="hero-eyebrow">{selectedAuditRecord.kind}</span>
                <h4>{auditRecordId(selectedAuditRecord)}</h4>
                <p>Tenant-lokaler Audit-Record mit Hash, Previous-Hash und redacted Payload.</p>
              </div>
              <div className="run-focus-meta">
                <span className="pill muted">{selectedAuditRecord.action}</span>
                <code>{selectedAuditRecord.audit_id}</code>
                <code>{selectedAuditRecord.hash.slice(0, 16)}</code>
              </div>
              <div className="run-focus-grid">
                <div><span>Tenant</span><strong>{selectedAuditRecord.tenant_id}</strong></div>
                <div><span>Kind</span><strong>{selectedAuditRecord.kind}</strong></div>
                <div><span>Action</span><strong>{selectedAuditRecord.action}</strong></div>
                <div><span>Zeit</span><strong>{formatRunTime(selectedAuditRecord.ts)}</strong></div>
                <div><span>Item</span><strong>{auditRecordId(selectedAuditRecord)}</strong></div>
                <div><span>Hash</span><strong>{selectedAuditRecord.hash.slice(0, 16)}</strong></div>
                <div><span>Previous</span><strong>{selectedAuditRecord.previous_hash?.slice(0, 16) ?? "root"}</strong></div>
                <div><span>Payload</span><strong>{Object.keys(selectedAuditRecord.item ?? {}).length} Felder</strong></div>
                <div><span>Lineage</span><strong>{selectedAuditLineage?.refs?.length ?? "lade"}</strong></div>
              </div>
              <div className="run-focus-actions">
                {auditRecordItemString(selectedAuditRecord, "run_id") && (
                  <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(auditRecordItemString(selectedAuditRecord, "run_id") ?? "")}`}>Run öffnen</Link>
                )}
                {auditRecordItemString(selectedAuditRecord, "evidence_id") && (
                  <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(auditRecordItemString(selectedAuditRecord, "evidence_id") ?? "")}`}>Evidence öffnen</Link>
                )}
                {auditRecordDeepLinks(selectedAuditRecord).map((link) => (
                  <Link key={`${link.label}:${link.to}`} className="btn ghost small" to={link.to}>{link.label}</Link>
                ))}
                {selectedAuditLineage?.refs?.filter((ref) => ref.route).slice(0, 6).map((ref) => (
                  <Link key={`${ref.ref_type}:${ref.item_id}:${ref.route}`} className="btn ghost small" to={ref.route ?? "/app/agents"}>
                    {ref.ref_type}
                  </Link>
                ))}
              </div>
              {selectedAuditLineage && (
                <ul className="lineage-list compact">
                  {(selectedAuditLineage.refs ?? []).slice(0, 8).map((ref) => (
                    <li key={ref.ref}>
                      <code>{ref.ref_type}</code>
                      <span>{ref.item_id ?? ref.ref}</span>
                      <span className={`pill ${ref.exists ? "good" : "muted"}`}>{ref.exists ? "resolved" : "external"}</span>
                    </li>
                  ))}
                </ul>
              )}
              <pre className="audit-record-payload">{JSON.stringify(selectedAuditRecord.item, null, 2).slice(0, 1400)}</pre>
            </div>
          )}

          {recordFocusMissing && (
            <div className="empty-state">Dieser Audit-Record ist im aktuellen Tenant und Audit-Kind nicht sichtbar.</div>
          )}
          {recordDetail.isError && !selectedAuditRecord && !recordFocusMissing && (
            <p className="error">Audit-Record-Detail konnte nicht geladen werden.</p>
          )}
        </div>
      )}

      <ul className="audit-record-list">
        {auditRecords.map((record) => {
          const recordId = auditRecordId(record);
          const runId = auditRecordItemString(record, "run_id");
          const evidenceId = auditRecordItemString(record, "evidence_id");
          const deepLinks = auditRecordDeepLinks(record);
          const active = Boolean(recordFocus && auditRecordMatches(record, recordFocus));
          return (
            <li key={record.audit_id} className={`audit-record-item ${active ? "active" : ""}`}>
              <div className="audit-record-main">
                <strong>{recordId}</strong>
                <span className="pill muted">{record.action}</span>
                <span className="muted small">{new Date(record.ts).toLocaleString("de-DE")}</span>
              </div>
              <div className="audit-record-meta">
                <code>{record.hash.slice(0, 16)}</code>
                {record.previous_hash && <span>prev <code>{record.previous_hash.slice(0, 12)}</code></span>}
              </div>
              <div className="audit-record-links">
                <Link className="btn ghost small" to={`/app/agents?kind=${encodeURIComponent(kind)}&record=${encodeURIComponent(recordId)}`}>Record öffnen</Link>
                {runId && <Link className="btn ghost small" to={`/app/agents?run=${encodeURIComponent(runId)}`}>Run</Link>}
                {evidenceId && <Link className="btn ghost small" to={`/app/agents?evidence=${encodeURIComponent(evidenceId)}`}>Evidence</Link>}
                {deepLinks.map((link) => (
                  <Link key={`${record.audit_id}:${link.label}:${link.to}`} className="btn ghost small" to={link.to}>{link.label}</Link>
                ))}
              </div>
              <pre>{JSON.stringify(record.item, null, 2).slice(0, 700)}</pre>
            </li>
          );
        })}
      </ul>

      {records.isLoading && <p className="muted">Audit-Records werden geladen...</p>}
      {!records.isLoading && (records.data ?? []).length === 0 && (
        <p className="muted">Keine Records für diesen Tenant und Filter.</p>
      )}
    </section>
  );
}

function TenantIsolationEvidencePanel({
  status,
  loading,
  error,
}: {
  status?: TenantIsolationStatus;
  loading: boolean;
  error: unknown;
}) {
  const checks = status?.checks ?? [];
  const blockers = checks.filter((check) => check.release_blocking && ["fail", "not_configured"].includes(check.status));
  const warnings = checks.filter((check) => check.status === "warning");
  const visibleChecks = [...checks]
    .sort((a, b) => Number(b.release_blocking) - Number(a.release_blocking) || a.layer.localeCompare(b.layer))
    .slice(0, 8);
  const state = blockers.length ? "fail" : status?.status ?? (loading ? "loading" : "unknown");

  return (
    <div className={`tenant-evidence-gate ${state}`}>
      <div className="section-head compact">
        <div>
          <h3>Tenant Isolation Release Gate</h3>
          <p className="muted small">
            Mandantengrenzen, Modellpolitik und Evidence-Scope werden direkt neben den Agent-Runs geprüft.
          </p>
        </div>
        <span className={`status-pill ${state === "pass" ? "good" : state === "loading" ? "muted" : "warning"}`}>
          {loading ? "lade" : blockers.length ? "blocked" : status?.status ?? "offen"}
        </span>
      </div>

      <div className="tenant-evidence-summary">
        <div>
          <span>Tenant</span>
          <strong>{status?.tenant_id ?? "..."}</strong>
        </div>
        <div>
          <span>Checks</span>
          <strong>{checks.length || "..."}</strong>
        </div>
        <div>
          <span>Release Blocker</span>
          <strong>{blockers.length}</strong>
        </div>
        <div>
          <span>Warnings</span>
          <strong>{warnings.length}</strong>
        </div>
        <div>
          <span>External Models</span>
          <strong>{status?.external_model_policy ?? "policy-gated"}</strong>
        </div>
        <div>
          <span>Audit Namespace</span>
          <strong>{status?.audit_namespace ?? "..."}</strong>
        </div>
      </div>

      <div className="tenant-evidence-checks">
        {visibleChecks.map((check) => (
          <Link key={check.id} to="/app/compliance" className={`tenant-evidence-check ${check.status}`}>
            <span>{check.layer}</span>
            <strong>{check.title}</strong>
            <small>{check.release_blocking ? "release-blocking" : check.status}</small>
          </Link>
        ))}
      </div>

      {Boolean(error) && <p className="error">Tenant-Isolation-Status konnte nicht geladen werden.</p>}
      {!loading && status && visibleChecks.length === 0 && <p className="muted">Noch keine Tenant-Isolation-Checks für diesen Tenant.</p>}
    </div>
  );
}

function AuditIntegrityPanel({
  report,
  loading,
  error,
  currentKind,
  onSelectKind,
}: {
  report?: AuditIntegrityReport;
  loading: boolean;
  error: unknown;
  currentKind: AuditKind;
  onSelectKind: (kind: AuditKind) => void;
}) {
  const chains = report?.chains ?? [];
  const activeChains = chains.filter((chain) => chain.records > 0);
  const brokenChains = chains.filter((chain) => chain.enabled && !chain.verified);
  const featuredChains = [...chains]
    .sort((a, b) => {
      const brokenDelta = Number(b.enabled && !b.verified) - Number(a.enabled && !a.verified);
      if (brokenDelta) return brokenDelta;
      return b.records - a.records;
    })
    .slice(0, 8);

  return (
    <div className={`audit-integrity-panel ${report?.verified ? "verified" : brokenChains.length ? "broken" : "pending"}`}>
      <div className="section-head compact">
        <div>
          <h3>Audit Integrity Report</h3>
          <p className="muted small">
            Gesamtstatus über alle Audit-Ketten des aktuellen Tenants. Broken Chains sind release-blocking.
          </p>
        </div>
        <span className={`status-pill ${report?.verified ? "good" : loading ? "muted" : "warning"}`}>
          {loading ? "lade" : report?.verified ? "verified" : report?.enabled ? "action required" : "disabled"}
        </span>
      </div>

      <div className="audit-integrity-summary">
        <div>
          <span>Checked Kinds</span>
          <strong>{report?.checked_kinds ?? "..."}</strong>
        </div>
        <div>
          <span>Verified</span>
          <strong>{report?.verified_kinds ?? "..."}</strong>
        </div>
        <div>
          <span>Broken</span>
          <strong>{report?.broken_kinds ?? "..."}</strong>
        </div>
        <div>
          <span>Total Records</span>
          <strong>{(report?.total_records ?? 0).toLocaleString("de-DE")}</strong>
        </div>
        <div>
          <span>Active Chains</span>
          <strong>{activeChains.length}</strong>
        </div>
      </div>

      <div className="audit-chain-overview" aria-label="Audit chain overview">
        {featuredChains.map((chain) => {
          const kindValue = chain.kind;
          const supportedKind = isAuditKind(kindValue);
          return (
            <button
              key={kindValue}
              type="button"
              className={`audit-chain-chip ${chain.verified ? "verified" : "broken"} ${kindValue === currentKind ? "active" : ""}`}
              onClick={() => supportedKind && onSelectKind(kindValue)}
              disabled={!supportedKind}
            >
              <span>{supportedKind ? AUDIT_KIND_LABEL[kindValue] : kindValue}</span>
              <strong>{chain.records.toLocaleString("de-DE")}</strong>
              <small>{chain.verified ? "verified" : chain.error ?? "broken"}</small>
            </button>
          );
        })}
      </div>

      {Boolean(error) && <p className="error">Audit-Integrity-Report konnte nicht geladen werden.</p>}
      {!loading && report && featuredChains.length === 0 && <p className="muted">Noch keine Audit-Ketten für diesen Tenant.</p>}
    </div>
  );
}
